import { useState, useEffect } from 'react';
import { Check, Users, CalendarDays, Minus, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900', text: 'text-white', textMuted: 'text-slate-400',
    card: 'bg-slate-800', border: 'border-slate-700', accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet', text: 'text-[#f8f0e6]', textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt', border: 'border-[#3b2125]', accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]', text: 'text-[#1c1710]', textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]', border: 'border-[#e2d8c3]', accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white', text: 'text-slate-900', textMuted: 'text-slate-500',
    card: 'bg-slate-50', border: 'border-slate-200', accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50', text: 'text-purple-900', textMuted: 'text-purple-600',
    card: 'bg-white', border: 'border-pink-200', accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900', text: 'text-stone-50', textMuted: 'text-stone-400',
    card: 'bg-stone-800', border: 'border-stone-700', accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900', text: 'text-white', textMuted: 'text-zinc-400',
    card: 'bg-zinc-800', border: 'border-zinc-700', accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

const SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

const fmtPrice = (amount, currency) =>
  `${SYMBOLS[currency] || '£'}${Number(amount).toLocaleString('en-GB')}`;

const RetreatBookingCard = ({ retreat, avail, currency, t, theme, primaryColor, siteId, idx }) => {
  const options = retreat.price_options || [];
  const [optionId, setOptionId] = useState(options[0]?.id || '');
  const [qty, setQty] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const placesLeft = avail ? avail.places_left : null;
  const bookable = (avail ? avail.booking_open : retreat.booking_open) && options.some(o => o.price > 0);
  const soldOut = bookable && placesLeft !== null && placesLeft <= 0;
  const selected = options.find(o => o.id === optionId) || options[0];
  const maxQty = Math.max(1, Math.min(6, placesLeft ?? 6));
  const isLight = ['ivory_gold', 'light_modern', 'colorful_kids'].includes(theme);

  const handleBook = async () => {
    if (!name.trim() || !email.trim() || !email.includes('@')) {
      toast.error('Please enter your name and a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/retreats/${siteId}/checkout`, {
        retreat_id: retreat.id,
        price_option_id: selected.id,
        quantity: qty,
        name: name.trim(),
        email: email.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start booking — please try again');
      setSubmitting(false);
    }
  };

  return (
    <AnimatedSection animation="fadeUp">
      <div
        className={cn("rounded-3xl border overflow-hidden shadow-xl", t.card, t.border)}
        data-testid={`retreat-card-${idx}`}
      >
        <div className="grid md:grid-cols-2">
          {retreat.image && (
            <div className="relative min-h-[280px] md:min-h-full">
              <img src={retreat.image} alt={retreat.title} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}
          <div className="p-8 md:p-10">
            {retreat.date_label && (
              <p
                className="text-sm uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2"
                style={{ color: primaryColor }}
              >
                <CalendarDays className="w-4 h-4" /> {retreat.date_label}
              </p>
            )}
            <h3 className={cn("text-2xl md:text-3xl font-bold mb-4", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
              {retreat.title}
            </h3>
            {retreat.description && (
              <p className={cn("leading-relaxed mb-6", t.textMuted)}>{retreat.description}</p>
            )}
            {(retreat.includes || []).length > 0 && (
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mb-8">
                {retreat.includes.map((inc, i) => (
                  <li key={i} className={cn("flex items-start gap-2 text-sm", t.text)}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                    {inc}
                  </li>
                ))}
              </ul>
            )}

            {bookable ? (
              <div className={cn("border-t pt-6", t.border)}>
                {/* Places left badge */}
                {placesLeft !== null && (
                  <div className="mb-5" data-testid={`places-left-badge-${idx}`}>
                    {soldOut ? (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-red-600/10 text-red-600 border border-red-600/30">
                        Sold Out
                      </span>
                    ) : placesLeft <= 5 ? (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600 border border-amber-500/40 animate-pulse">
                        <Users className="w-3.5 h-3.5" /> Only {placesLeft} {placesLeft === 1 ? 'place' : 'places'} left
                      </span>
                    ) : (
                      <span className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border", t.textMuted, t.border)}>
                        <Users className="w-3.5 h-3.5" /> {placesLeft} of {avail.capacity} places available
                      </span>
                    )}
                  </div>
                )}

                {!soldOut && (
                  <>
                    {/* Price options */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {options.map((o) => (
                        <button
                          key={o.id}
                          onClick={() => setOptionId(o.id)}
                          className={cn(
                            "px-4 py-2.5 rounded-xl border text-sm font-medium transition-all text-left",
                            (selected?.id === o.id)
                              ? "shadow-md"
                              : cn("opacity-70 hover:opacity-100", t.border)
                          )}
                          style={selected?.id === o.id ? { borderColor: primaryColor, boxShadow: `0 4px 14px -4px ${primaryColor}60` } : {}}
                          data-testid={`retreat-option-${idx}-${o.id}`}
                        >
                          <span className={cn("block", t.text)}>{o.label}</span>
                          <span className="block font-bold" style={{ color: primaryColor }}>
                            {fmtPrice(o.price, currency)} <span className={cn("font-normal text-xs", t.textMuted)}>pp</span>
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Quantity + total */}
                    <div className="flex items-center gap-4 mb-5">
                      <div className={cn("flex items-center rounded-xl border", t.border)}>
                        <button
                          className={cn("p-2.5 disabled:opacity-30", t.text)}
                          onClick={() => setQty(Math.max(1, qty - 1))}
                          disabled={qty <= 1}
                          data-testid={`retreat-qty-minus-${idx}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className={cn("w-10 text-center font-bold", t.text)} data-testid={`retreat-qty-${idx}`}>{qty}</span>
                        <button
                          className={cn("p-2.5 disabled:opacity-30", t.text)}
                          onClick={() => setQty(Math.min(maxQty, qty + 1))}
                          disabled={qty >= maxQty}
                          data-testid={`retreat-qty-plus-${idx}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <p className={cn("text-xs uppercase tracking-wider", t.textMuted)}>Total</p>
                        <p className={cn("text-xl font-bold", t.text)} data-testid={`retreat-total-${idx}`}>
                          {fmtPrice((selected?.price || 0) * qty, currency)}
                        </p>
                      </div>
                    </div>

                    {/* Booking form / button */}
                    {!showForm ? (
                      <Button
                        className={cn("rounded-full px-8 py-6 text-base text-white w-full sm:w-auto", t.button)}
                        onClick={() => setShowForm(true)}
                        data-testid={`retreat-book-btn-${idx}`}
                      >
                        Book Your Place
                      </Button>
                    ) : (
                      <div className="space-y-3" data-testid={`retreat-booking-form-${idx}`}>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Input
                            placeholder="Your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={cn("rounded-xl", isLight ? "bg-white border-[#e2d8c3] text-[#1c1710]" : "")}
                            data-testid={`booking-name-input-${idx}`}
                          />
                          <Input
                            type="email"
                            placeholder="Email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={cn("rounded-xl", isLight ? "bg-white border-[#e2d8c3] text-[#1c1710]" : "")}
                            data-testid={`booking-email-input-${idx}`}
                          />
                        </div>
                        <Button
                          className={cn("rounded-full px-8 py-6 text-base text-white w-full sm:w-auto", t.button)}
                          onClick={handleBook}
                          disabled={submitting}
                          data-testid={`booking-confirm-btn-${idx}`}
                        >
                          {submitting ? (
                            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Taking you to secure payment…</span>
                          ) : (
                            `Continue to Payment — ${fmtPrice((selected?.price || 0) * qty, currency)}`
                          )}
                        </Button>
                        <p className={cn("text-xs", t.textMuted)}>Secure card payment powered by Stripe.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className={cn("border-t pt-6", t.border)}>
                <Button
                  variant="outline"
                  className={cn("rounded-full px-8 py-6 text-base", t.text, t.border)}
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid={`retreat-interest-btn-${idx}`}
                >
                  Register Your Interest
                </Button>
                <p className={cn("text-xs mt-2", t.textMuted)}>Booking opens soon — get in touch to be first to know.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export const RetreatsSection = ({ data, theme, primaryColor, siteId }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const [availability, setAvailability] = useState({});
  const retreats = data?.retreats || [];
  const currency = data?.currency || 'gbp';

  useEffect(() => {
    if (!siteId || retreats.length === 0) return;
    api.get(`/retreats/${siteId}/availability`)
      .then(res => setAvailability(res.data.availability || {}))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  if (retreats.length === 0) return null;

  const upcoming = retreats.filter(r => !r.is_past);
  const past = retreats.filter(r => r.is_past);

  return (
    <section id="retreats" className={cn("py-32 px-4", t.bg)} data-testid="retreats-section">
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-14">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Residential Retreats
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Retreats'}
          </h2>
          {data.intro && (
            <p className={cn("mt-5 max-w-2xl mx-auto leading-relaxed", t.textMuted)}>{data.intro}</p>
          )}
        </AnimatedSection>

        <div className="space-y-10">
          {upcoming.map((retreat, idx) => (
            <RetreatBookingCard
              key={retreat.id || idx}
              retreat={retreat}
              avail={availability[retreat.id]}
              currency={currency}
              t={t}
              theme={theme}
              primaryColor={primaryColor}
              siteId={siteId}
              idx={idx}
            />
          ))}
        </div>

        {past.length > 0 && (
          <div className="mt-20" data-testid="past-retreats-section">
            <h3 className={cn("text-2xl font-bold mb-8 text-center opacity-70", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
              Previous Retreats
            </h3>
            <div className={cn("grid gap-6 mx-auto", past.length === 1 ? "grid-cols-1 max-w-md" : "sm:grid-cols-2 max-w-4xl")}>
              {past.map((retreat, idx) => (
                <div
                  key={retreat.id || idx}
                  className={cn("rounded-2xl border overflow-hidden group", t.card, t.border)}
                  data-testid={`past-retreat-${idx}`}
                >
                  {retreat.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={retreat.image}
                        alt={retreat.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-black/70 text-white border border-white/30">
                        Sold Out
                      </span>
                    </div>
                  )}
                  <div className="p-6">
                    {retreat.date_label && (
                      <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: primaryColor }}>
                        {retreat.date_label}
                      </p>
                    )}
                    <h4 className={cn("font-bold text-lg", t.text)}>{retreat.title}</h4>
                    {retreat.description && (
                      <p className={cn("text-sm mt-2 leading-relaxed line-clamp-3", t.textMuted)}>{retreat.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
